import { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import OnboardingLayout from "../components/OnboardingLayout/OnboardingLayout";
import Spinner from "../components/Spinner/Spinner";
import BusinessDesign from "./BusinessDesign";
import BusinessType from "./BusinessType";
import TagSelect from "./TagSelect";
import CreateService from "./CreateService";
import Availability from "./Availability";
import Success from "./Success";
import styles from "../styles/finishSetup.module.css";

// The "עוד קצת וסיימנו" WhatsApp link (/finish#<code>): its own limited session,
// never the login page. Opens the business details screen (Instagram, photos,
// colors, description, live page), then asks only for what's still missing
// before the page can take bookings.

// public/index.html moves the code out of the address before analytics load
const readCode = () => {
  try {
    const stored = sessionStorage.getItem("finish_code");
    if (stored) return stored;
  } catch (_) {}
  return window.location.hash.slice(1);
};

// Availability reloads the page after creating the default hours, so the place in
// the flow has to survive a reload
const PROGRESS_KEY = "finish_progress";
const readProgress = (businessId) => {
  try {
    const p = JSON.parse(sessionStorage.getItem(PROGRESS_KEY));
    return p && p.businessId === businessId ? p : null;
  } catch (_) {
    return null;
  }
};

const flowFor = (s) => [
  ...(s.registered ? [] : ["who"]),
  "design",
  ...(s.services ? [] : ["type", "tags", "services"]),
  ...(s.hours ? [] : ["hours"]),
];

const FinishSetup = () => {
  const [screen, setScreen] = useState("opening"); // opening | invalid | live | steps
  const [message, setMessage] = useState("");
  const [business, setBusiness] = useState(null);
  const [flow, setFlow] = useState([]);
  const [index, setIndex] = useState(0);
  const [stepData, setStepData] = useState({});
  const [active, setActive] = useState(false);

  useEffect(() => {
    const code = readCode();
    if (!code) {
      setScreen("invalid");
      return;
    }
    axios
      .post(`${BASE_URL}/onboarding/finish/open`, { code })
      .then(({ data }) => {
        const b = data.business;
        setActive(!!data.active);
        localStorage.setItem("auth_token", data.token);
        localStorage.setItem("business_id", b.id);
        localStorage.setItem("business_code", b.businessId);
        localStorage.setItem("user_type", String(b.type));
        localStorage.setItem("ownerName", b.ownerName || "");
        // Left by another signup on this device
        localStorage.removeItem("generatedServices");
        localStorage.removeItem("businessType");
        setBusiness(b);
        const progress = readProgress(b.businessId);
        if (progress) {
          setFlow(progress.flow);
          setIndex(progress.index);
          setStepData(progress.stepData);
        } else {
          setFlow(flowFor(data.status));
        }
        setScreen("steps");
      })
      .catch((e) => {
        setMessage(e?.response?.status === 403 ? "" : e?.response?.data?.message || "");
        setScreen("invalid");
      });
  }, []);

  useEffect(() => {
    if (screen !== "steps") return;
    try {
      sessionStorage.setItem(PROGRESS_KEY, JSON.stringify({ businessId: business.businessId, flow, index, stepData }));
    } catch (_) {}
  }, [screen, business, flow, index, stepData]);

  const finish = async () => {
    setScreen("opening");
    try {
      const { data } = await axios.post(
        `${BASE_URL}/onboarding/finish/done`,
        {},
        { headers: { Authorization: localStorage.getItem("auth_token") } }
      );
      if (data.ready) {
        sessionStorage.removeItem(PROGRESS_KEY);
        setScreen("live");
        return;
      }
      // Something was skipped on the way: back to just that
      setFlow(flowFor(data.status).filter((s) => s !== "design"));
      setIndex(0);
      setScreen("steps");
    } catch (_) {
      setIndex(flow.length - 1);
      setScreen("steps");
    }
  };

  const next = (data) => {
    if (data) setStepData((d) => ({ ...d, ...data }));
    window.scrollTo(0, 0);
    if (index + 1 < flow.length) setIndex(index + 1);
    else finish();
  };
  const back = () => {
    window.scrollTo(0, 0);
    setIndex((i) => Math.max(i - 1, 0));
  };

  if (screen === "opening") {
    return (
      <div className={styles.center}>
        <Spinner />
      </div>
    );
  }
  if (screen === "live") return <Success live updated={active} />;
  if (screen === "invalid") {
    return (
      <div className={styles.center} dir="rtl">
        <div className={styles.card}>
          <h1 className={styles.title}>הקישור הזה כבר לא פעיל</h1>
          <p className={styles.text}>{message || "מתחברים עם מספר הטלפון וממשיכים מאיפה שהפסקת."}</p>
          <a className={styles.primary} href="/register">
            להתחברות
          </a>
        </div>
      </div>
    );
  }

  const step = flow[index];
  const canGoBack = index > 0 && ["design", "tags", "services", "hours"].includes(step);
  return (
    <OnboardingLayout
      stepGroup={["type", "tags", "services", "hours"].includes(step) ? 2 : 1}
      onBack={canGoBack ? back : null}
      showSteps
    >
      {step === "who" && <WhoStep business={business} onDone={next} />}
      {step === "design" && (
        <BusinessDesign
          finish
          instagram={business.instagram}
          finishLabel={index === flow.length - 1 ? "סיום!" : "ממשיכים ←"}
          onNext={next}
          onBack={back}
        />
      )}
      {step === "type" && <BusinessType onNext={next} onBack={back} />}
      {step === "tags" && <TagSelect onNext={next} onBack={back} stepData={stepData} />}
      {step === "services" && <CreateService onNext={next} onBack={back} />}
      {step === "hours" && <Availability onNext={next} onBack={back} />}
    </OnboardingLayout>
  );
};

// Owners who stopped right after the OTP never gave a name. This also does what
// the signup form does (first_time starts the free plan), so the app stops
// asking them to register.
const WhoStep = ({ business, onDone }) => {
  const [businessName, setBusinessName] = useState(business.businessName || "");
  const [ownerName, setOwnerName] = useState(business.ownerName || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!businessName.trim() || !ownerName.trim() || saving) return;
    setSaving(true);
    setError("");
    try {
      await axios.put(
        `${BASE_URL}/business/update_profile_info`,
        {
          id: business.id,
          address: "ישראל",
          business_name: businessName.trim(),
          name: ownerName.trim(),
          // The number clients see on the page
          ...(business.businessPhone ? {} : { business_phone: business.phone }),
          first_time: true,
        },
        { headers: { Authorization: localStorage.getItem("auth_token") } }
      );
      localStorage.setItem("ownerName", ownerName.trim());
      onDone();
    } catch (e) {
      setError(e?.response?.data?.message || "לא הצלחנו לשמור, נסו שוב");
      setSaving(false);
    }
  };

  return (
    <div className={styles.page} dir="rtl">
      <h1 className={styles.title}>עוד קצת וסיימנו! איך קוראים לעסק?</h1>
      <p className={styles.text}>זה השם שהלקוחות יראו בעמוד.</p>
      <label className={styles.label} htmlFor="finish-business">
        שם העסק
      </label>
      <input id="finish-business" className={styles.input} value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
      <label className={styles.label} htmlFor="finish-owner">
        השם שלך
      </label>
      <input id="finish-owner" className={styles.input} value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
      {error && <div className={styles.error}>{error}</div>}
      <button type="button" className={styles.primary} onClick={save} disabled={saving || !businessName.trim() || !ownerName.trim()}>
        {saving ? "שומרים..." : "ממשיכים"}
      </button>
    </div>
  );
};

export default FinishSetup;
